import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';

/** Dropped near the top of a channel setup screen (WhatsApp/Facebook Messenger/X-Twitter/Phone)
 * — renders nothing when the screen was opened normally (e.g. from "Complete your profile"),
 * and a small "back to setup steps" bar when it was opened FROM the channel onboarding stepper
 * (routeParams.onCompleteScreen set), since those screens have no other way to return to the
 * stepper to skip/continue once inside. */
export const StepperReturnBanner: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams } = useAppContext();
  const { t } = useLocale();

  const onCompleteScreen = (routeParams as any)?.onCompleteScreen;
  const onCompleteParams = (routeParams as any)?.onCompleteParams;
  // Android Back inside a channel setup opened from onboarding returns to
  // the channel list, same as this banner.
  useHardwareBackAction(onCompleteScreen ? () => navigateTo(onCompleteScreen, undefined, onCompleteParams) : undefined);
  if (!onCompleteScreen) return null;

  return (
    <TouchableOpacity
      style={styles.banner}
      onPress={() => navigateTo(onCompleteScreen, undefined, onCompleteParams)}
      activeOpacity={0.8}
    >
      <DirectionalIcon name="chevron-back" size={18} color={colors.primary} />
      <Text style={styles.text}>{t('auth.backToSetupSteps') || 'Back to setup steps'}</Text>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.screenPadding,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primaryTransparent,
  },
  text: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
});
